import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Badge
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  Folder as FolderIcon,
  Check as CheckIcon,
  PlayArrow as PlayIcon,
  Groups as GroupsIcon,
  EmojiEvents as TrophyIcon,
  Assignment as ModuleIcon
} from '@mui/icons-material';
import trackingService from '../../services/trackingService';

// Design System Colors (matching TrackingDashboard)
const colors = {
  primary: '#485869',
  primaryLight: '#6c7c8c',
  primaryDark: '#3A4754',
  success: '#66CC33',
  error: '#fa5b5b',
  warning: '#FF9933',
  info: '#4DBCF3',
  background: '#F5F6F7',
  paper: '#FFFFFF',
  textPrimary: '#485869',
  textSecondary: '#6B757F',
  grey50: '#F5F6F7',
  grey200: '#C8CDD2',
  grey500: '#6B757F',
  grey700: '#485869',
};

// Role colors for hackathon team members
const roleColors = {
  senior_dev: '#8B5CF6',    // Purple
  junior_dev: '#3B82F6',    // Blue
  fa_operator: '#10B981',   // Green
  test_operator: '#F59E0B', // Orange
  designer: '#EC4899',      // Pink
  dev_operator: '#3B82F6',
};

// Stats Card Component
const StatCard = ({ title, value, subtitle, icon, color = colors.primary, loading = false }) => (
  <Card
    elevation={2}
    sx={{
      height: '100%',
      bgcolor: colors.paper,
      borderRadius: 2,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)' }
    }}
  >
    <CardContent sx={{ p: 2 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} sx={{ color }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500, fontSize: '0.75rem' }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ color, fontWeight: 700 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.6, fontSize: 28 }}>
            {icon}
          </Box>
        </Box>
      )}
    </CardContent>
  </Card>
);

// Helper function to group events into blocks (same as TrackingDashboard)
const groupEventsIntoBlocks = (events) => {
  const blocks = [];
  let currentBlock = null;

  for (const event of events) {
    if (event.event_type === 'UserPromptSubmit') {
      if (currentBlock) {
        blocks.push(currentBlock);
      }
      currentBlock = {
        startEvent: event,
        toolEvents: [],
        stopEvent: null
      };
    } else if (event.event_type === 'Stop') {
      if (currentBlock) {
        currentBlock.stopEvent = event;
        blocks.push(currentBlock);
        currentBlock = null;
      }
    } else if (event.event_type === 'PreToolUse' || event.event_type === 'PostToolUse') {
      if (currentBlock) {
        currentBlock.toolEvents.push(event);
      }
    }
  }

  if (currentBlock) {
    blocks.push(currentBlock);
  }

  return blocks;
};

// User Event Block Component
const UserEventBlock = ({ block, blockIndex, showUser = false }) => {
  const [expanded, setExpanded] = useState(false);
  const { startEvent, toolEvents, stopEvent } = block;

  const startTime = new Date(startEvent.timestamp);
  const endTime = stopEvent ? new Date(stopEvent.timestamp) : null;
  const duration = endTime ? Math.round((endTime - startTime) / 1000) : null;

  const promptPreview = startEvent.prompt
    ? startEvent.prompt.substring(0, 120) + (startEvent.prompt.length > 120 ? '...' : '')
    : 'No prompt';

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        border: `1px solid ${colors.grey200}`,
        borderRadius: 1,
        overflow: 'hidden',
        '&:hover': { borderColor: colors.primaryLight }
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: colors.grey50,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ color: colors.textSecondary, p: 0.5 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>

        <Chip
          label={`#${blockIndex + 1}`}
          size="small"
          sx={{ bgcolor: colors.primary, color: 'white', fontWeight: 700, height: 22, fontSize: '0.7rem' }}
        />

        {showUser && startEvent.user_name && (
          <Chip
            icon={<PersonIcon sx={{ fontSize: 12 }} />}
            label={startEvent.user_name}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.65rem' }}
          />
        )}

        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
          {startTime.toLocaleTimeString()}
        </Typography>

        <Chip
          label={`${toolEvents.length} tools`}
          size="small"
          variant="outlined"
          sx={{ borderColor: colors.warning, color: colors.warning, height: 20, fontSize: '0.65rem' }}
        />

        {duration !== null && (
          <Chip
            label={`${duration}s`}
            size="small"
            variant="outlined"
            sx={{ borderColor: colors.success, color: colors.success, height: 20, fontSize: '0.65rem' }}
          />
        )}

        <Chip
          label={stopEvent ? 'Done' : 'Running'}
          size="small"
          sx={{
            bgcolor: stopEvent ? colors.success : colors.warning,
            color: 'white',
            height: 20,
            fontSize: '0.65rem',
            ml: 'auto'
          }}
        />
      </Box>

      <Box sx={{ px: 1.5, py: 1, bgcolor: colors.paper }}>
        <Typography variant="body2" sx={{ color: colors.textPrimary, fontSize: '0.8rem', fontStyle: 'italic' }}>
          "{promptPreview}"
        </Typography>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1.5, bgcolor: colors.paper, borderTop: `1px solid ${colors.grey200}` }}>
          {/* Full Prompt */}
          {startEvent.prompt && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
                Full Prompt:
              </Typography>
              <Paper elevation={0} sx={{ p: 1, mt: 0.5, bgcolor: colors.grey50, maxHeight: 150, overflow: 'auto', fontSize: '0.75rem' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                  {startEvent.prompt}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Tool Events */}
          {toolEvents.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
                Tool Calls ({toolEvents.length}):
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {toolEvents.slice(0, 10).map((event, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      px: 1,
                      bgcolor: event.event_type === 'PreToolUse' ? 'rgba(77, 188, 243, 0.05)' : 'rgba(102, 204, 51, 0.05)',
                      borderRadius: 0.5,
                      mb: 0.5
                    }}
                  >
                    <Chip
                      label={event.event_type === 'PreToolUse' ? 'Pre' : 'Post'}
                      size="small"
                      sx={{
                        bgcolor: event.event_type === 'PreToolUse' ? colors.info : colors.success,
                        color: 'white',
                        height: 18,
                        fontSize: '0.6rem'
                      }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      {event.tool_name || '-'}
                    </Typography>
                    {event.file_path && (
                      <Typography variant="caption" sx={{ color: colors.textSecondary, ml: 'auto' }} noWrap>
                        {event.file_path.split('/').slice(-2).join('/')}
                      </Typography>
                    )}
                  </Box>
                ))}
                {toolEvents.length > 10 && (
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    +{toolEvents.length - 10} more...
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Response */}
          {stopEvent && stopEvent.tool_response && (
            <Box>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
                Response:
              </Typography>
              <Paper elevation={0} sx={{ p: 1, mt: 0.5, bgcolor: colors.grey50, maxHeight: 150, overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: '0.7rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {typeof stopEvent.tool_response === 'string'
                    ? stopEvent.tool_response.substring(0, 500)
                    : JSON.stringify(stopEvent.tool_response, null, 2).substring(0, 500)}
                  {(typeof stopEvent.tool_response === 'string' ? stopEvent.tool_response.length : JSON.stringify(stopEvent.tool_response).length) > 500 && '...'}
                </pre>
              </Paper>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// User Card Component (for individual user view)
const UserCard = ({ user }) => {
  const [expanded, setExpanded] = useState(false);
  const blocks = useMemo(() => groupEventsIntoBlocks(user.events), [user.events]);

  return (
    <Card elevation={2} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
      {/* User Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: colors.grey50,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: `1px solid ${colors.grey200}`
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ color: colors.textSecondary }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>

        <PersonIcon sx={{ color: colors.primary }} />

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary }}>
            {user.name}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            {user.email}
          </Typography>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Conversations">
            <Chip
              icon={<PlayIcon sx={{ fontSize: 14 }} />}
              label={blocks.length}
              size="small"
              sx={{ bgcolor: colors.info, color: 'white', fontWeight: 600 }}
            />
          </Tooltip>
          <Tooltip title="Tool Calls">
            <Chip
              icon={<CodeIcon sx={{ fontSize: 14 }} />}
              label={user.stats.tool_calls}
              size="small"
              variant="outlined"
              sx={{ borderColor: colors.warning, color: colors.warning }}
            />
          </Tooltip>
          <Tooltip title="Completed">
            <Chip
              icon={<CheckIcon sx={{ fontSize: 14 }} />}
              label={user.stats.completed}
              size="small"
              sx={{ bgcolor: colors.success, color: 'white' }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Projects */}
      {user.projects && user.projects.length > 0 && (
        <Box sx={{ px: 2, py: 1, bgcolor: colors.paper, borderBottom: `1px solid ${colors.grey200}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FolderIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
            {user.projects.slice(0, 3).map((project, idx) => (
              <Chip
                key={idx}
                label={project.split('/').slice(-2).join('/')}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            ))}
            {user.projects.length > 3 && (
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                +{user.projects.length - 3} more
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Event Blocks */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2, bgcolor: colors.paper, maxHeight: 500, overflow: 'auto' }}>
          {blocks.length > 0 ? (
            blocks.map((block, idx) => (
              <UserEventBlock key={idx} block={block} blockIndex={idx} />
            ))
          ) : (
            <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', py: 2 }}>
              No conversation blocks found
            </Typography>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

// Team Member Card (for hackathon team view)
const TeamMemberCard = ({ member, roleColor }) => {
  const [expanded, setExpanded] = useState(false);
  const blocks = useMemo(() => groupEventsIntoBlocks(member.events || []), [member.events]);

  const hasActivity = member.events && member.events.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        border: `1px solid ${colors.grey200}`,
        borderLeft: `4px solid ${roleColor}`,
        borderRadius: 1,
        overflow: 'hidden',
        opacity: hasActivity ? 1 : 0.6
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: colors.paper,
          cursor: hasActivity ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
        onClick={() => hasActivity && setExpanded(!expanded)}
      >
        {hasActivity && (
          <IconButton size="small" sx={{ color: colors.textSecondary, p: 0.5 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}

        <PersonIcon sx={{ color: roleColor, fontSize: 20 }} />

        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
            {member.name}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            {member.email}
          </Typography>
        </Box>

        <Chip
          label={member.role?.replace('_', ' ') || 'Member'}
          size="small"
          sx={{
            bgcolor: roleColor,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.65rem',
            height: 22,
            textTransform: 'capitalize'
          }}
        />

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Prompts">
            <Chip
              label={member.stats?.prompts || 0}
              size="small"
              sx={{ bgcolor: colors.info, color: 'white', height: 20, fontSize: '0.65rem', minWidth: 28 }}
            />
          </Tooltip>
          <Tooltip title="Tool Calls">
            <Chip
              label={member.stats?.tool_calls || 0}
              size="small"
              sx={{ bgcolor: colors.warning, color: 'white', height: 20, fontSize: '0.65rem', minWidth: 28 }}
            />
          </Tooltip>
          <Tooltip title="Completed">
            <Chip
              label={member.stats?.completed || 0}
              size="small"
              sx={{ bgcolor: colors.success, color: 'white', height: 20, fontSize: '0.65rem', minWidth: 28 }}
            />
          </Tooltip>
        </Box>
      </Box>

      {hasActivity && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ p: 1.5, bgcolor: colors.grey50, maxHeight: 400, overflow: 'auto' }}>
            {blocks.map((block, idx) => (
              <UserEventBlock key={idx} block={block} blockIndex={idx} />
            ))}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

// Hackathon Team Card Component
const HackathonTeamCard = ({ team }) => {
  const [expanded, setExpanded] = useState(false);
  const blocks = useMemo(() => groupEventsIntoBlocks(team.events || []), [team.events]);

  return (
    <Card elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
      {/* Team Header */}
      <Box
        sx={{
          p: 2,
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ color: 'white' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>

        <Badge badgeContent={team.team_number} color="warning" sx={{ '& .MuiBadge-badge': { fontWeight: 700 } }}>
          <GroupsIcon sx={{ color: 'white', fontSize: 32 }} />
        </Badge>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
            {team.team_name}
          </Typography>
          {team.assigned_module && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <ModuleIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {team.assigned_module}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Team Stats */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Members">
            <Chip
              icon={<PersonIcon sx={{ fontSize: 14, color: 'white' }} />}
              label={team.members?.length || 0}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
            />
          </Tooltip>
          <Tooltip title="Prompts">
            <Chip
              icon={<PlayIcon sx={{ fontSize: 14, color: 'white' }} />}
              label={team.stats?.prompts || 0}
              size="small"
              sx={{ bgcolor: colors.info, color: 'white', fontWeight: 600 }}
            />
          </Tooltip>
          <Tooltip title="Tool Calls">
            <Chip
              icon={<CodeIcon sx={{ fontSize: 14, color: 'white' }} />}
              label={team.stats?.tool_calls || 0}
              size="small"
              sx={{ bgcolor: colors.warning, color: 'white', fontWeight: 600 }}
            />
          </Tooltip>
          <Tooltip title="Completed">
            <Chip
              icon={<CheckIcon sx={{ fontSize: 14, color: 'white' }} />}
              label={team.stats?.completed || 0}
              size="small"
              sx={{ bgcolor: colors.success, color: 'white', fontWeight: 600 }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Team Members */}
      <Box sx={{ p: 2, bgcolor: colors.grey50, borderBottom: `1px solid ${colors.grey200}` }}>
        <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1.5, fontWeight: 600 }}>
          Team Members ({team.members?.length || 0})
        </Typography>
        {team.members?.map((member, idx) => (
          <TeamMemberCard
            key={member.email || idx}
            member={member}
            roleColor={roleColors[member.role] || colors.primary}
          />
        ))}
        {(!team.members || team.members.length === 0) && (
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontStyle: 'italic' }}>
            No members assigned
          </Typography>
        )}
      </Box>

      {/* Team Event Blocks (Combined) */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2, bgcolor: colors.paper }}>
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1.5, fontWeight: 600 }}>
            All Team Activity ({blocks.length} conversations)
          </Typography>
          <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
            {blocks.length > 0 ? (
              blocks.map((block, idx) => (
                <UserEventBlock key={idx} block={block} blockIndex={idx} showUser={true} />
              ))
            ) : (
              <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', py: 2 }}>
                No team activity recorded yet
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
};

// Main Component
const TeamLogsTab = () => {
  const [viewMode, setViewMode] = useState(0); // 0 = Hackathon Teams, 1 = Individual Users
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(48);

  // Data for both views
  const [teamData, setTeamData] = useState(null);
  const [userData, setUserData] = useState(null);

  const fetchTeamData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await trackingService.getHackathonTeamLogs(hours);
      setTeamData(result);
    } catch (err) {
      console.error('Failed to fetch hackathon team logs:', err);
      setError(err.message || 'Failed to load hackathon team logs');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await trackingService.getTeamLogs(hours);
      setUserData(result);
    } catch (err) {
      console.error('Failed to fetch user logs:', err);
      setError(err.message || 'Failed to load user logs');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    if (viewMode === 0) {
      fetchTeamData();
    } else {
      fetchUserData();
    }
  }, [viewMode, fetchTeamData, fetchUserData]);

  // Calculate totals for hackathon teams
  const teamTotals = useMemo(() => {
    if (!teamData || !teamData.teams) return { prompts: 0, tools: 0, completed: 0 };
    return teamData.teams.reduce((acc, team) => ({
      prompts: acc.prompts + (team.stats?.prompts || 0),
      tools: acc.tools + (team.stats?.tool_calls || 0),
      completed: acc.completed + (team.stats?.completed || 0)
    }), { prompts: 0, tools: 0, completed: 0 });
  }, [teamData]);

  // Calculate totals for individual users
  const userTotals = useMemo(() => {
    if (!userData || !userData.users) return { prompts: 0, tools: 0, completed: 0 };
    return userData.users.reduce((acc, user) => ({
      prompts: acc.prompts + (user.stats?.prompts || 0),
      tools: acc.tools + (user.stats?.tool_calls || 0),
      completed: acc.completed + (user.stats?.completed || 0)
    }), { prompts: 0, tools: 0, completed: 0 });
  }, [userData]);

  const handleRefresh = () => {
    if (viewMode === 0) {
      fetchTeamData();
    } else {
      fetchUserData();
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
            Team Logs
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            {viewMode === 0
              ? `Hackathon: ${teamData?.event_name || 'Loading...'}`
              : 'Monitor team activity and Claude Code usage across all users'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              label="Time Range"
            >
              <MenuItem value={1}>Last 1 hour</MenuItem>
              <MenuItem value={6}>Last 6 hours</MenuItem>
              <MenuItem value={12}>Last 12 hours</MenuItem>
              <MenuItem value={24}>Last 24 hours</MenuItem>
              <MenuItem value={48}>Last 48 hours</MenuItem>
              <MenuItem value={168}>Last 7 days</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              borderColor: colors.primary,
              color: colors.primary,
              textTransform: 'none',
              '&:hover': { borderColor: colors.primaryDark, bgcolor: colors.grey50 }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* View Mode Tabs */}
      <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={viewMode}
          onChange={(e, v) => setViewMode(v)}
          sx={{
            '& .MuiTab-root': {
              color: colors.textSecondary,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem'
            },
            '& .Mui-selected': { color: colors.primary },
            '& .MuiTabs-indicator': { bgcolor: colors.primary }
          }}
        >
          <Tab
            label="Hackathon Teams"
            icon={<GroupsIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            label="Individual Users"
            icon={<PersonIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress sx={{ borderRadius: 1 }} />
        </Box>
      )}

      {/* Hackathon Teams View */}
      {viewMode === 0 && !loading && teamData && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Active Teams"
                value={teamData.total_teams || 0}
                subtitle={teamData.event_status}
                icon={<GroupsIcon />}
                color={colors.primary}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Total Prompts"
                value={teamTotals.prompts}
                subtitle="All teams"
                icon={<PlayIcon />}
                color={colors.info}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Tool Calls"
                value={teamTotals.tools}
                subtitle="Pre + Post"
                icon={<CodeIcon />}
                color={colors.warning}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Completed"
                value={teamTotals.completed}
                subtitle="Stop events"
                icon={<CheckIcon />}
                color={colors.success}
              />
            </Grid>
          </Grid>

          {/* Team Cards */}
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
            Showing {teamData.teams?.length || 0} teams with {teamData.total_events || 0} total events
          </Typography>

          {teamData.teams && teamData.teams.length > 0 ? (
            teamData.teams.map((team, idx) => (
              <HackathonTeamCard key={team.team_id || idx} team={team} />
            ))
          ) : (
            <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
              <GroupsIcon sx={{ fontSize: 48, color: colors.grey500, mb: 2 }} />
              <Typography sx={{ color: colors.textSecondary }}>
                {teamData.message || 'No hackathon teams found'}
              </Typography>
            </Paper>
          )}

          {/* Unassigned Users */}
          {teamData.unassigned_users && teamData.unassigned_users.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 2, fontWeight: 600 }}>
                Unassigned Users ({teamData.unassigned_users.length})
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                Users with activity but not assigned to any hackathon team
              </Typography>
              {teamData.unassigned_users.map((user, idx) => (
                <UserCard key={user.email || idx} user={user} />
              ))}
            </Box>
          )}
        </>
      )}

      {/* Individual Users View */}
      {viewMode === 1 && !loading && userData && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Active Users"
                value={userData.total_users || 0}
                subtitle={`Last ${hours}h`}
                icon={<PersonIcon />}
                color={colors.primary}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Total Prompts"
                value={userTotals.prompts}
                subtitle="UserPromptSubmit"
                icon={<PlayIcon />}
                color={colors.info}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Tool Calls"
                value={userTotals.tools}
                subtitle="Pre + Post"
                icon={<CodeIcon />}
                color={colors.warning}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Completed"
                value={userTotals.completed}
                subtitle="Stop events"
                icon={<CheckIcon />}
                color={colors.success}
              />
            </Grid>
          </Grid>

          {/* User Cards */}
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
            Showing {userData.users?.length || 0} active users with {userData.total_events || 0} events
          </Typography>

          {userData.users && userData.users.length > 0 ? (
            userData.users.map((user, idx) => (
              <UserCard key={user.email || idx} user={user} />
            ))
          ) : (
            <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
              <PersonIcon sx={{ fontSize: 48, color: colors.grey500, mb: 2 }} />
              <Typography sx={{ color: colors.textSecondary }}>
                No activity found in the last {hours} hours
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default TeamLogsTab;
